<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-clipboard-text-clock</v-icon>
            Resumen Jobs - Observabilidad
            <v-spacer></v-spacer>
            <v-btn icon small class="mr-2" @click="showHelp = true" title="Ayuda: significado de los estados">
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
            <v-btn color="primary" small @click="fetchJobs" :loading="loading">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-card-title>

          <!-- Estadísticas -->
          <v-card-text v-if="stats">
            <v-row>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2">
                  <div class="text-h5">{{ stats.tocas_unicas || 0 }}</div>
                  <div class="caption">Tocas únicas</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2" color="green lighten-5">
                  <div class="text-h5 green--text">{{ stats.exitosas_directas || 0 }}</div>
                  <div class="caption">Exitosas (1er intento)</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2" color="light-blue lighten-5">
                  <div class="text-h5 light-blue--text">{{ stats.corregidas || 0 }}</div>
                  <div class="caption">Corregidas</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2" color="red lighten-5">
                  <div class="text-h5 red--text">{{ stats.pendientes || 0 }}</div>
                  <div class="caption">Pendientes</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2" color="orange lighten-5">
                  <div class="text-h5 orange--text">{{ stats.processing || 0 }}</div>
                  <div class="caption">En proceso</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="2">
                <v-card outlined class="text-center pa-2" color="grey lighten-4">
                  <div class="text-h5 grey--text text--darken-1">{{ stats.total_jobs || 0 }}</div>
                  <div class="caption">Total jobs</div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Reenviar toca manualmente -->
          <v-card-text class="pt-0">
            <v-row dense align="center">
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="reenviarTocaId"
                  label="ID de toca a reenviar"
                  dense
                  outlined
                  type="number"
                  hint="Ingresa el ID de la toca para regenerar su resumen"
                  @keyup.enter="reenviarToca"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn
                  color="orange"
                  dark
                  :disabled="!reenviarTocaId"
                  :loading="reenviando"
                  @click="reenviarToca"
                >
                  <v-icon left small>mdi-send-clock</v-icon>
                  Reenviar a generar resumen
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <v-alert v-if="reenviarResult" dense text :type="reenviarResult.type" class="mb-0">
                  {{ reenviarResult.message }}
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>

          <!-- Filtros -->
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Status"
                  clearable
                  dense
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.callbackStatus"
                  :items="callbackStatusOptions"
                  label="Callback Status"
                  clearable
                  dense
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="filters.fecha_ini"
                  label="Fecha inicio"
                  type="date"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="filters.fecha_fin"
                  label="Fecha fin"
                  type="date"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-btn color="primary" block @click="fetchJobs" :loading="loading">
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Tabla -->
          <v-data-table
            :headers="headers"
            :items="jobs"
            :loading="loading"
            :items-per-page="20"
            class="elevation-0"
            dense
          >
            <!-- Toca numero/periodo -->
            <template v-slot:item.toca_numero="{ item }">
              <span v-if="item.toca_info">{{ item.toca_info.numero }}/{{ item.toca_info.periodo }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <!-- Sala -->
            <template v-slot:item.toca_sala="{ item }">
              <span v-if="item.toca_info && item.toca_info.sala" class="text-caption">{{ item.toca_info.sala }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <!-- Status -->
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                dark
                x-small
              >
                {{ item.status }}
              </v-chip>
            </template>

            <!-- Error -->
            <template v-slot:item.errorType="{ item }">
              <span v-if="item.errorType" class="red--text text-caption">
                {{ item.errorType }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>

            <!-- Documentos (PDF + Resumen) -->
            <template v-slot:item.docs="{ item }">
              <v-btn
                v-if="item.toca_info && item.toca_info.has_file"
                icon x-small
                color="red"
                title="Ver PDF original"
                @click="openFile(item.recordId, 'pdf')"
              >
                <v-icon x-small>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn
                v-if="item.toca_info && item.toca_info.has_resumen"
                icon x-small
                color="blue"
                title="Ver Resumen IA"
                @click="openFile(item.recordId, 'txt')"
              >
                <v-icon x-small>mdi-file-document</v-icon>
              </v-btn>
            </template>

            <!-- Callback Status -->
            <template v-slot:item.callbackStatus="{ item }">
              <v-icon v-if="item.callbackStatus === 'success'" small color="green">mdi-check-circle</v-icon>
              <v-icon v-else-if="item.callbackStatus === 'failed'" small color="red">mdi-close-circle</v-icon>
              <v-icon v-else small color="grey">mdi-clock-outline</v-icon>
              <span class="ml-1 text-caption">{{ item.callbackStatus }}</span>
            </template>

            <!-- Attempts -->
            <template v-slot:item.attempts="{ item }">
              <v-chip x-small :color="item.attempts > 1 ? 'orange' : 'grey'" dark>
                {{ item.attempts || 1 }}
              </v-chip>
            </template>

            <!-- Fecha -->
            <template v-slot:item.createdAt="{ item }">
              <span class="text-caption">{{ formatDate(item.createdAt) }}</span>
            </template>

            <!-- Acciones -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="(item.status === 'failed' && item.attempts >= 3) || item.callbackStatus === 'failed'"
                x-small
                color="orange"
                dark
                @click="retryJob(item)"
                :loading="retryingJob === item.jobId"
              >
                <v-icon x-small left>mdi-refresh</v-icon>
                Reintentar
              </v-btn>
              <span v-else-if="item.status === 'failed' && item.attempts < 3" class="text-caption grey--text">
                Reintentando ({{ item.attempts }}/3)
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Modal de Ayuda -->
    <v-dialog v-model="showHelp" max-width="700" scrollable>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-information</v-icon>
          Guía de Estados
          <v-spacer></v-spacer>
          <v-btn icon @click="showHelp = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="max-height: 500px;">

          <h4 class="mb-2">Estado del proceso (Status)</h4>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr>
                <td><v-chip x-small color="orange" dark>processing</v-chip></td>
                <td>La Lambda está procesando el PDF (descargando, generando resumen con IA)</td>
              </tr>
              <tr>
                <td><v-chip x-small color="green" dark>completed</v-chip></td>
                <td>El resumen se generó exitosamente y el .txt ya está en S3</td>
              </tr>
              <tr>
                <td><v-chip x-small color="red" dark>failed</v-chip></td>
                <td>El proceso falló. Ver columna "Error" para conocer la causa</td>
              </tr>
            </tbody>
          </v-simple-table>

          <h4 class="mb-2">Estado del Callback (notificación a la app)</h4>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr>
                <td><v-icon small color="green">mdi-check-circle</v-icon> success</td>
                <td>La app fue notificada y la base de datos se actualizó correctamente</td>
              </tr>
              <tr>
                <td><v-icon small color="red">mdi-close-circle</v-icon> failed</td>
                <td>El webhook falló, pero el resumen SÍ existe en S3. Usar "Reintentar" para sincronizar la BD</td>
              </tr>
              <tr>
                <td><v-icon small color="grey">mdi-clock-outline</v-icon> not_attempted</td>
                <td>Aún no se intentó notificar (proceso en curso o falló antes)</td>
              </tr>
              <tr>
                <td><v-icon small color="grey">mdi-link-off</v-icon> no_url</td>
                <td>No se envió notificación porque el mensaje no traía URL de callback</td>
              </tr>
            </tbody>
          </v-simple-table>

          <h4 class="mb-2">Tipos de Error</h4>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr>
                <td class="red--text text-caption">invalid_message</td>
                <td>El mensaje en la cola estaba mal formado o le faltaban campos</td>
              </tr>
              <tr>
                <td class="red--text text-caption">s3_download_error</td>
                <td>El PDF no se encontró en S3 (no existe o sin permisos)</td>
              </tr>
              <tr>
                <td class="red--text text-caption">noctua_auth_error</td>
                <td>No se pudo autenticar con Noctua (credenciales inválidas o servicio caído)</td>
              </tr>
              <tr>
                <td class="red--text text-caption">noctua_processing_error</td>
                <td>Noctua no pudo generar el resumen (timeout, error interno)</td>
              </tr>
              <tr>
                <td class="red--text text-caption">s3_upload_error</td>
                <td>No se pudo subir el .txt a S3 (permisos o error de red)</td>
              </tr>
            </tbody>
          </v-simple-table>

          <h4 class="mb-2">¿Qué hacer?</h4>
          <v-simple-table dense>
            <tbody>
              <tr>
                <td><b>Status failed</b></td>
                <td>Click "Reintentar" → reenvía a la cola para procesar de nuevo</td>
              </tr>
              <tr>
                <td><b>Callback failed</b></td>
                <td>Click "Reintentar" → sincroniza la BD directamente (el resumen ya existe en S3)</td>
              </tr>
              <tr>
                <td><b>Processing (atorado)</b></td>
                <td>Si lleva más de 5 min, puede estar atorado. Revisar logs de la Lambda en CloudWatch</td>
              </tr>
            </tbody>
          </v-simple-table>

        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ApiService from "@/services/ApiService";

export default {
  name: "ResumenJobs",
  data() {
    return {
      jobs: [],
      stats: null,
      loading: false,
      retryingJob: null,
      showHelp: false,
      reenviarTocaId: null,
      reenviando: false,
      reenviarResult: null,
      filters: {
        status: null,
        callbackStatus: null,
        fecha_ini: null,
        fecha_fin: null,
      },
      headers: [
        { text: "Toca ID", value: "recordId", width: "70px" },
        { text: "Toca", value: "toca_numero", width: "100px" },
        { text: "Sala", value: "toca_sala", width: "150px" },
        { text: "Status", value: "status", width: "100px" },
        { text: "Error", value: "errorType", width: "150px" },
        { text: "Docs", value: "docs", width: "100px", sortable: false },
        { text: "Callback", value: "callbackStatus", width: "120px" },
        { text: "Intentos", value: "attempts", width: "80px", align: "center" },
        { text: "Fecha", value: "createdAt", width: "150px" },
        { text: "Acciones", value: "actions", sortable: false, width: "120px" },
      ],
      statusOptions: [
        { text: "Completado", value: "completed" },
        { text: "Fallido", value: "failed" },
        { text: "En proceso", value: "processing" },
      ],
      callbackStatusOptions: [
        { text: "Exitoso", value: "success" },
        { text: "Fallido", value: "failed" },
        { text: "No intentado", value: "not_attempted" },
      ],
      snackbar: {
        show: false,
        text: "",
        color: "success",
      },
    };
  },
  mounted() {
    this.fetchJobs();
  },
  methods: {
    async fetchJobs() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.callbackStatus) params.callbackStatus = this.filters.callbackStatus;
        if (this.filters.fecha_ini) params.fecha_ini = this.filters.fecha_ini;
        if (this.filters.fecha_fin) params.fecha_fin = this.filters.fecha_fin;

        const response = await ApiService.get({ url: "/resumen-jobs", data: params });
        this.jobs = response.data.data.jobs || [];
        this.stats = response.data.data.stats || null;
      } catch (error) {
        console.error("Error cargando jobs:", error);
        this.showSnackbar("Error al cargar los jobs", "error");
      } finally {
        this.loading = false;
      }
    },
    async retryJob(job) {
      this.retryingJob = job.jobId;
      try {
        const response = await ApiService.postJSON({
          url: `/resumen-jobs/${job.jobId}/retry`,
          data: {},
        });
        this.showSnackbar(response.data.data.message || "Job reenviado", "success");
        this.fetchJobs();
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || "Error al reintentar";
        this.showSnackbar(msg, "error");
      } finally {
        this.retryingJob = null;
      }
    },
    async reenviarToca() {
      if (!this.reenviarTocaId) return;
      this.reenviando = true;
      this.reenviarResult = null;
      try {
        var response = await ApiService.postJSON({
          url: "/tocas-reenviar-resumen",
          data: { toca_id: parseInt(this.reenviarTocaId) },
        });
        var data = response.data.data;
        this.reenviarResult = {
          type: "success",
          message: data.message + (data.toca ? " (" + data.toca.numero + "/" + data.toca.periodo + ")" : "")
        };
        this.reenviarTocaId = null;
        this.fetchJobs();
      } catch (error) {
        var msg = (error.response && error.response.data && error.response.data.message) || "Error al reenviar";
        this.reenviarResult = { type: "error", message: msg };
      } finally {
        this.reenviando = false;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "completed": return "green";
        case "failed": return "red";
        case "processing": return "orange";
        default: return "grey";
      }
    },
    async openFile(tocaId, type) {
      try {
        var response;
        if (type === 'pdf') {
          response = await ApiService.get({ url: "/toca/" + tocaId + "/file", data: { modo: "visualizar" } });
        } else {
          response = await ApiService.get({ url: "/toca/" + tocaId + "/file_resumen" });
        }
        var url = response.data.data.url;
        if (url) {
          window.open(url, "_blank");
        }
      } catch (error) {
        this.showSnackbar("Error al obtener el archivo", "error");
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      return date.toLocaleString("es-MX", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>
